/**
 * EscopoDoRobo — "O que o robô mostra" (spec 2026-09-24, 4.3; TASK-11447).
 *
 * O dono escolhe UMA organização e, dentro dela, "todos os tenants" (a
 * organização como ela for, incluindo tenant novo) ou uma lista. Vale para o
 * robô inteiro: painel e notificações de todas as fontes.
 *
 * Quem decide o acesso é o backend, em toda leitura: esta tela só oferece o
 * que `/escopo/opcoes` devolve, e um tenant marcado a que o dono perdeu
 * acesso aparece com o selo "Sem acesso" em vez de sumir calado.
 */
import { useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Checkbox, Group, List, Radio, Select, Stack, Text } from '@mantine/core'
import { IconAlertTriangle } from '@tabler/icons-react'
import { isAxiosError } from 'axios'
import { useQueryClient } from '@tanstack/react-query'

import { Callout, DataCard } from '@/components/ui'
import {
  definirEscopo, reciboDoDono, useEscopoDoMeuRobo, useOpcoesDeEscopo,
  type AvisoDoAjuste, type HardwareModel, type ModoDeEscopo, type RotaDoRobo,
} from '@/api/robo'
import { ReciboDoAviso } from './ReciboDoAviso'
import { rotuloDoRobo } from './avisoDoAjuste'

const FRASES: Record<string, string> = {
  organizacao_sem_acesso: 'Você não enxerga nenhum tenant desta organização, então o robô não teria o que mostrar dela.',
  tenant_fora_da_organizacao: 'Um dos tenants marcados não pertence a esta organização. Recarregue a página e marque de novo.',
  tenant_sem_acesso: 'Um dos tenants marcados não está no seu acesso. Desmarque-o e salve de novo.',
  lista_vazia: 'Marque ao menos um tenant, ou escolha "Todos os tenants da organização".',
}

function fraseDoErro(err: unknown): string {
  if (isAxiosError(err)) {
    const detalhe = (err.response?.data as { detail?: unknown } | undefined)?.detail
    if (typeof detalhe === 'string' && FRASES[detalhe]) return FRASES[detalhe]
  }
  return 'Não foi possível salvar agora. Tente de novo em instantes.'
}

type Props = {
  /** Revisão R2: por qual caminho o robô consultou na última hora. */
  rota?: RotaDoRobo | null
  /** O tenant que a rota antiga serve (o do cadastro). */
  tenantDoCadastro?: string | null
  /** O modelo do robô, para o recibo do aviso dizer "O Robô II reagiu". */
  modelo?: HardwareModel | null
  /** No wizard de recebimento (spec 2026-09-27, 4.4): a diferença entre os dois modos, antes do formulário. */
  explicar?: boolean
}

export function EscopoDoRobo({ rota = null, tenantDoCadastro = null, modelo = null, explicar = false }: Props = {}) {
  const { data: escopo, isLoading: carregandoEscopo } = useEscopoDoMeuRobo()
  const {
    data: opcoes, isLoading: carregandoOpcoes, isError: opcoesFalharam, refetch: recarregarOpcoes,
  } = useOpcoesDeEscopo()
  const qc = useQueryClient()

  const [orgId, setOrgId] = useState<string | null>(null)
  const [modo, setModo] = useState<ModoDeEscopo>('todos')
  const [marcados, setMarcados] = useState<string[]>([])
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [salvo, setSalvo] = useState(false)
  const [aviso, setAviso] = useState<AvisoDoAjuste | null>(null)

  const gravado = escopo?.gravado ?? null
  useEffect(() => {
    if (!gravado) return
    setOrgId(String(gravado.organization_id))
    setModo(gravado.modo)
    setMarcados(gravado.tenant_ids.map(String))
  }, [gravado])

  const organizacoes = useMemo(() => {
    const lista = (opcoes ?? []).map((o) => ({ value: String(o.id), label: o.nome }))
    // A organização gravada pode ter saído das opções (o dono perdeu acesso a
    // todos os tenants dela). Ela continua aparecendo, para a tela dizer o
    // que está gravado em vez de um campo vazio.
    if (gravado && !lista.some((o) => o.value === String(gravado.organization_id))) {
      lista.push({
        value: String(gravado.organization_id),
        label: escopo?.organizacao_nome ?? `organização #${gravado.organization_id}`,
      })
    }
    return lista
  }, [opcoes, gravado, escopo?.organizacao_nome])

  const semAcesso = new Set((escopo?.sem_acesso ?? []).map(String))
  const semDados = new Set((escopo?.sem_dados ?? []).map(String))
  const tenantsDaOrg = useMemo(() => {
    const org = (opcoes ?? []).find((o) => String(o.id) === orgId)
    const lista = (org?.tenants ?? []).map((t) => ({
      id: String(t.id),
      nome: t.nome,
      monitorado: t.monitorado !== false,
    }))
    // Marcado e sem acesso: fica na lista, com o selo, para o dono decidir.
    if (gravado && String(gravado.organization_id) === orgId) {
      for (const t of gravado.tenant_ids) {
        if (!lista.some((x) => x.id === String(t))) {
          lista.push({
            id: String(t),
            nome: escopo?.nomes?.[String(t)] ?? `tenant #${t}`,
            monitorado: true,
          })
        }
      }
    }
    return lista
  }, [opcoes, orgId, gravado, escopo?.nomes])

  if (carregandoEscopo || carregandoOpcoes) {
    return (
      <DataCard title="O que o robô mostra">
        <Text size="sm" c="dimmed">Carregando…</Text>
      </DataCard>
    )
  }

  const podeSalvar = !!orgId && (modo === 'todos' || marcados.length > 0)

  const salvar = async () => {
    if (!orgId) return
    setErro(null)
    setSalvo(false)
    setAviso(null)
    setSalvando(true)
    try {
      const salvoAgora = await definirEscopo({
        organization_id: Number(orgId),
        modo,
        tenant_ids: modo === 'lista' ? marcados.map(Number).sort((a, b) => a - b) : [],
      })
      // O prefixo `['meu-robo']` pega o escopo E o robô: é do robô que o
      // wizard lê `recebimento.pendencias` para destravar o "Continuar".
      await qc.invalidateQueries({ queryKey: ['meu-robo'] })
      setSalvo(true)
      setAviso(salvoAgora.aviso ?? null)
    } catch (err) {
      setErro(fraseDoErro(err))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <DataCard title="O que o robô mostra">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Uma organização e os tenants dela. Vale para tudo o que o robô mostra:
          o painel e as notificações de todas as fontes. O robô só mostra o que
          você mesmo pode ver — se perder acesso a um tenant, ele sai sozinho.
        </Text>

        {explicar && (
          <List size="sm" spacing={4}>
            <List.Item>
              <b>Organização inteira:</b> todos os tenants dela, inclusive os que entrarem depois.
            </List.Item>
            <List.Item>
              <b>Tenants escolhidos:</b> só os marcados. Um tenant novo fica de fora até você marcar.
            </List.Item>
            <List.Item>
              Nos dois casos, o robô só mostra o que a sua permissão já deixa você ver no Console.
            </List.Item>
          </List>
        )}

        {/* O escopo só vale na rota nova (revisão R2). Sem este aviso o dono
            escolhe e o visor não muda. A marca vem do que o robô FAZ. */}
        {rota === 'antiga' && (
          <Callout variant="warning">
            {tenantDoCadastro
              ? `Este robô ainda usa a configuração antiga e mostra ${tenantDoCadastro}.`
              : 'Este robô ainda usa a configuração antiga e não mostra alerta de ambiente nenhum.'}
            {' '}Escolha o que mostrar e então configure pela USB para ele seguir esta escolha.
          </Callout>
        )}

        {escopo?.motivo === 'sem_escopo' && (
          <Callout variant="info">
            Escolha o que o robô mostra. Até lá ele não mostra alerta de
            ambiente nenhum — só as mensagens endereçadas a você.
          </Callout>
        )}
        {escopo?.motivo === 'sem_acesso' && (
          <Callout variant="warning">
            Você não tem mais acesso a nenhum dos tenants escolhidos, então o
            robô não mostra alerta de ambiente nenhum. Escolha de novo.
          </Callout>
        )}
        {semDados.size > 0 && (
          <Callout variant="warning">
            O robô não está recebendo dados de{' '}
            {[...semDados].map((t) => escopo?.nomes?.[t] ?? `tenant #${t}`).join(', ')}.{' '}
            {semDados.size >= (escopo?.efetivo.length ?? 0)
              ? 'Ele não tem alerta nenhum para mostrar e avisa no visor qual ambiente falta.'
              : 'Ele mostra os alertas dos outros e avisa no visor qual ambiente falta.'}
          </Callout>
        )}

        {erro && <Alert color="red" icon={<IconAlertTriangle size={18} />}>{erro}</Alert>}
        {salvo && (
          <Alert color="green">
            {rota === 'antiga'
              ? 'Salvo. O robô passa a mostrar esta escolha depois de configurado pela USB.'
              : 'Salvo. O robô passa a mostrar esta escolha.'}
          </Alert>
        )}
        {salvo && aviso && (
          <ReciboDoAviso
            key={aviso.id ?? aviso.estado}
            aviso={aviso}
            caminho={aviso.id ? reciboDoDono(aviso.id) : null}
            robo={rotuloDoRobo(modelo)}
          />
        )}

        {opcoesFalharam ? (
          // Erro não é ausência: dizer "sem acesso" aqui é afirmar algo que
          // não se sabe, e trava o wizard sem saída (achado em prd, 28/09).
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            <Stack gap="xs" align="flex-start">
              <Text size="sm">Não foi possível carregar as organizações agora.</Text>
              <Button size="xs" variant="light" onClick={() => { void recarregarOpcoes() }}>
                Tentar de novo
              </Button>
            </Stack>
          </Alert>
        ) : organizacoes.length === 0 ? (
          <Text size="sm" c="dimmed">
            Você não tem acesso a nenhum tenant de organização alguma, então não
            há o que escolher. Quem concede acesso é quem administra o seu perfil.
          </Text>
        ) : (
          <>
            <Select
              label="Organização"
              placeholder="Escolha a organização"
              data={organizacoes}
              value={orgId}
              onChange={(v) => {
                setOrgId(v)
                setMarcados([])
                setSalvo(false)
              }}
              allowDeselect={false}
              comboboxProps={{ withinPortal: false }}
            />

            <Radio.Group
              label="Tenants"
              value={modo}
              onChange={(v) => { setModo(v as ModoDeEscopo); setSalvo(false) }}
            >
              <Stack gap="xs" mt={4}>
                <Radio value="todos" label="Todos os tenants da organização"
                  description="Inclui tenant novo que entrar nela." />
                <Radio value="lista" label="Escolher tenants" />
              </Stack>
            </Radio.Group>

            {modo === 'lista' && orgId && (
              <Checkbox.Group value={marcados} onChange={(v) => { setMarcados(v); setSalvo(false) }}>
                <Stack gap="xs">
                  {tenantsDaOrg.map((t) => (
                    <Checkbox
                      key={t.id}
                      value={t.id}
                      label={
                        <Group gap="xs" wrap="nowrap">
                          <span>{t.nome}</span>
                          {semAcesso.has(t.id) && (
                            <Badge color="gray" variant="light">Sem acesso</Badge>
                          )}
                          {semDados.has(t.id) && (
                            <Badge color="yellow" variant="light">Sem dados</Badge>
                          )}
                          {!t.monitorado && (
                            <Badge color="gray" variant="light">Sem monitoramento</Badge>
                          )}
                        </Group>
                      }
                    />
                  ))}
                </Stack>
              </Checkbox.Group>
            )}

            <Group>
              <Button disabled={!podeSalvar} loading={salvando} onClick={salvar}>
                Salvar
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </DataCard>
  )
}
